import { z } from "zod";
import { cardConciergeRequestSchema } from "./customer-cards.js";
import { customerConciergeRequestViewSchema } from "./domain-views.js";
import { staffMemoryEvidenceMessageSchema } from "./staff-customer-memory.js";
// The structured questions of a concierge request (CHT-751, `docs/architecture/milestone-d-contracts.md` 7.4 and 8, routes MD-R5, MD-R21
// and MD-R22). Staff ask a question with 1 to 8 typed fields; the customer answers the form (or, under the strict chat rule, the agent
// records the customer's own words). Every object is STRICT: a key that is not listed fails the parse, so a field added to a view later
// cannot reach a customer without someone naming it here too.
//
// The closed lists are owned by the domain (`request-question.ts`). This package cannot import it, so `apps/api` has a parity test that
// compares them with the domain's tuples and with the key lists of the application views.
const id = z.string().min(1).max(64);
export const questionFieldTypeValues = [
    "TEXT",
    "NUMBER",
    "DATE",
    "TIME",
    "CHOICE",
    "BOOLEAN",
];
export const questionStatusValues = ["OPEN", "ANSWERED", "WITHDRAWN"];
export const questionAnswerChannelValues = ["ACTION_CENTER", "CHAT", "STAFF_RECORDED"];
/** The bounds of the domain (`QUESTION_LIMITS`). */
export const QUESTION_SCHEMA_LIMITS = Object.freeze({
    maxFields: 8,
    promptChars: 1_000,
    keyChars: 32,
    labelChars: 100,
    optionChars: 60,
    minOptions: 2,
    maxOptions: 12,
    textAnswerChars: 2_000,
});
/**
 * One field of a question, as staff write it and as the customer reads it. `options` belongs to a `CHOICE` field only. The domain checks
 * the rest again (`validateQuestionFields`): distinct keys, distinct options, no control characters.
 */
export const requestQuestionFieldSchema = z.strictObject({
    key: z
        .string()
        .max(QUESTION_SCHEMA_LIMITS.keyChars)
        .regex(/^[a-z][a-zA-Z0-9]*$/),
    label: z.string().trim().min(1).max(QUESTION_SCHEMA_LIMITS.labelChars),
    type: z.enum(questionFieldTypeValues),
    required: z.boolean(),
    options: z
        .array(z.string().trim().min(1).max(QUESTION_SCHEMA_LIMITS.optionChars))
        .min(QUESTION_SCHEMA_LIMITS.minOptions)
        .max(QUESTION_SCHEMA_LIMITS.maxOptions)
        .optional(),
});
export const requestQuestionFieldsSchema = z
    .array(requestQuestionFieldSchema)
    .min(1)
    .max(QUESTION_SCHEMA_LIMITS.maxFields);
/** One answer value: the type of the field says which. The domain validates it again against the fields of the question. */
export const requestQuestionAnswerValueSchema = z.union([
    z.string().max(QUESTION_SCHEMA_LIMITS.textAnswerChars),
    z.number().finite(),
    z.boolean(),
]);
/** What the customer wrote, one value per field key. At most one value per field. */
export const requestQuestionAnswersSchema = z
    .record(z.string().max(QUESTION_SCHEMA_LIMITS.keyChars), requestQuestionAnswerValueSchema)
    .refine((answers) => Object.keys(answers).length <= QUESTION_SCHEMA_LIMITS.maxFields, {
    message: "too many answers",
});
/** MD-R21: `POST /customer/concierge-requests/:requestId/questions/:questionId/answer`. The key may come in the header instead. */
export const customerRequestAnswerBodySchema = z.strictObject({
    answers: requestQuestionAnswersSchema,
    /** One visible-ASCII key per form submit, the same rule as the `Idempotency-Key` header. When both are present they must be equal. */
    idempotencyKey: z
        .string()
        .min(1)
        .max(255)
        .regex(/^[\x21-\x7e]+$/)
        .nullish(),
});
/**
 * One question as a CUSTOMER sees it: the prompt, the fields, the status and, once answered, the customer's own answer. There is no asker,
 * no staff note, no message ref and no recorded reference in it.
 */
export const customerRequestQuestionSchema = z.strictObject({
    id,
    sequence: z.number().int().min(1),
    prompt: z.string(),
    fields: z.array(requestQuestionFieldSchema),
    status: z.enum(questionStatusValues),
    askedAt: z.date(),
    answeredAt: z.date().nullable(),
    answer: z.record(z.string(), requestQuestionAnswerValueSchema).nullable(),
    answerChannel: z.enum(questionAnswerChannelValues).nullable(),
});
/**
 * MD-R22 `GET /customer/concierge-requests/:requestId` and the answer of MD-R21: the request view of the customer plus the question they
 * can answer now (`null` when none is open) and the customer-safe history of questions, oldest first. The history holds the NEWEST 50
 * questions: `truncated` is true when older ones were left out. The open question is read on its own and is never left out.
 */
export const customerConciergeRequestDetailSchema = customerConciergeRequestViewSchema.extend({
    openQuestion: customerRequestQuestionSchema.nullable(),
    questions: z.array(customerRequestQuestionSchema),
    truncated: z.boolean(),
});
// --- the client's side of the wire: dates arrive as text ------------------------------------------------------------------------------
const wireInstant = z.coerce.date();
export const customerRequestQuestionWireSchema = z.object({
    id,
    sequence: z.number().int(),
    prompt: z.string(),
    fields: z.array(requestQuestionFieldSchema),
    status: z.enum(questionStatusValues),
    askedAt: wireInstant,
    answeredAt: wireInstant.nullable(),
    answer: z.record(z.string(), requestQuestionAnswerValueSchema).nullable(),
    answerChannel: z.enum(questionAnswerChannelValues).nullable(),
});
/** What a client reads of MD-R22 and of the answer of MD-R21: the card shape of the request, the open question and the history. */
export const customerRequestQuestionsWireSchema = cardConciergeRequestSchema.extend({
    openQuestion: customerRequestQuestionWireSchema.nullable(),
    questions: z.array(customerRequestQuestionWireSchema),
    /** True when older questions were left out of `questions` (the newest 50 are kept). A server that does not send it has not cut the list. */
    truncated: z.boolean().default(false),
});
// --- staff ------------------------------------------------------------------------------------------------------------------------
/**
 * One question as STAFF read it (`GET /staff/concierge-requests/:requestId/questions`): the asker, the Stream message of a chat answer and
 * the reference of an answer that staff recorded are here and in no customer shape.
 */
export const staffRequestQuestionSchema = z.strictObject({
    id,
    sequence: z.number().int().min(1),
    prompt: z.string(),
    fields: z.array(requestQuestionFieldSchema),
    status: z.enum(questionStatusValues),
    askedByStaffUserId: id,
    askedAt: z.string(),
    answeredAt: z.string().nullable(),
    withdrawnAt: z.string().nullable(),
    answer: z.record(z.string(), requestQuestionAnswerValueSchema).nullable(),
    answerChannel: z.enum(questionAnswerChannelValues).nullable(),
    /** The Stream message id of a chat answer. The admin puts it in the link that scrolls to the message; it never shows it as text. */
    sourceMessageRef: z.string().nullable(),
    /**
     * The chat message behind `sourceMessageRef` (conversation, sender, time, state), when Postgres knows it for the customer of the
     * request. `null` otherwise. NEVER its text: the text lives in Stream (CHT-1984, ADR 0012).
     */
    sourceMessage: staffMemoryEvidenceMessageSchema.nullable().default(null),
    recordedReference: z.string().nullable(),
});
export const staffRequestQuestionsSchema = z.strictObject({
    questions: z.array(staffRequestQuestionSchema),
});
